import { Checkbox, FormField } from 'semantic-ui-react';

/**
 * The consents a person gives for themselves.
 *
 * Every one is optional to save. A blank tick is a real answer — "not given" — and the whole
 * follow-up worklist (`/people` → "Missing H&S / data consent") exists to find them, so making
 * any of these required would hide the very people it is meant to surface.
 *
 * The three data-use ticks are deliberately separate rather than one "I agree": someone may be
 * happy to be counted in an aggregate and not to appear in a report.
 */

export interface ConsentState {
    picturePermission: boolean;
    consentDataAggregate: boolean;
    consentDataReporting: boolean;
    consentDataPrograms: boolean;
    consentDataCollection: boolean;
    consentHealthSafety: boolean;
    idSightedByCM: boolean;
}

export const EMPTY_CONSENT: ConsentState = {
    picturePermission: false,
    consentDataAggregate: false,
    consentDataReporting: false,
    consentDataPrograms: false,
    consentDataCollection: false,
    consentHealthSafety: false,
    idSightedByCM: false,
};

/** Label text lives here so the profile editor and this form cannot word them differently. */
export const CONSENT_LABELS: Array<{ key: keyof ConsentState; label: string; hint?: string }> = [
    {
        key: 'consentDataCollection',
        label: 'Consents to the collection and use of their data',
        hint: 'As set out in the notice and on the registration form.',
    },
    { key: 'consentDataAggregate', label: 'Data may be used in aggregate' },
    { key: 'consentDataReporting', label: 'Data may be used in reporting' },
    { key: 'consentDataPrograms', label: 'Data may be used to run our programs' },
    {
        key: 'consentHealthSafety',
        label: 'Briefed on and consents to the health & safety risks of their program',
    },
    { key: 'picturePermission', label: 'Picture permission' },
    { key: 'idSightedByCM', label: 'Their ID has been sighted by the centre manager' },
];

export const ConsentSection = ({
    value,
    onChange,
}: {
    value: ConsentState;
    onChange: (next: ConsentState) => void;
}) => (
    <div className="creator-section">
        <div className="creator-section-title">Consent</div>
        <p className="creator-section-hint">
            Leave anything not yet given unticked — people with gaps show up in the
            "Missing H&amp;S / data consent" filter on the People page.
        </p>
        <FormField>
            <div className="consent-toggles">
                {CONSENT_LABELS.map(({ key, label, hint }) => (
                    <div key={key} className="consent-row">
                        <Checkbox
                            toggle
                            // Semantic only emits `id`/`htmlFor` when given an id. Without one
                            // the input has no accessible name at all — invisible to a screen
                            // reader, and the label is clickable only through Semantic's own JS.
                            id={`consent-${key}`}
                            label={label}
                            checked={value[key]}
                            onChange={() => onChange({ ...value, [key]: !value[key] })}
                        />
                        {hint && <div className="consent-hint">{hint}</div>}
                    </div>
                ))}
            </div>
        </FormField>
    </div>
);
